import React from 'react';
import { Card } from '../ui/Card.tsx';
import { User, Calendar, Hash } from 'lucide-react';
import type { UserProfile } from '../../hooks/useUserProfile.ts';

interface AccountSectionProps {
  profile: UserProfile;
}

export const AccountSection: React.FC<AccountSectionProps> = ({ profile }) => {
  console.log('[AccountSection] Rendering with profile:', {
    hasUser: !!profile.user,
    email: profile.user?.email,
    loading: profile.loading,
  });

  if (profile.loading) {
    return (
      <Card className="p-6">
        <div className="h-6 bg-gray-200 rounded-md w-32 mb-4 animate-pulse"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded-md w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-3/4 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-1/2 animate-pulse"></div>
        </div>
      </Card>
    );
  }

  if (!profile.user) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Account Information
        </h3>
        <p className="text-gray-500">No account information available.</p>
      </Card>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatUserId = (userId: string) => {
    // Show first 8 and last 4 characters for debug purposes
    if (userId.length <= 12) return userId;
    return `${userId.slice(0, 8)}...${userId.slice(-4)}`;
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <User className="h-5 w-5 mr-2 text-blue-400" />
        Account Information
      </h3>
      
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-400">Email</span>
          <span className="text-sm text-gray-200">{profile.user.email || 'Not available'}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-400 flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            Account Created
          </span>
          <span className="text-sm text-gray-200">{formatDate(profile.user.created_at)}</span>
        </div>
        
        {profile.user.email_confirmed_at && (
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-400">Email Verified</span>
            <span className="text-sm text-green-400">
              {formatDate(profile.user.email_confirmed_at)}
            </span>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-400 flex items-center">
            <Hash className="h-4 w-4 mr-1" />
            User ID
          </span>
          <span className="text-xs font-mono text-gray-400 bg-gray-700 px-2 py-1 rounded">
            {formatUserId(profile.user.id)}
          </span>
        </div>
      </div>
    </Card>
  );
}; 